"use client";

import { Suspense, useCallback, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { Heart, Menu, Search, ShoppingCart, User } from "lucide-react";

import { createCartService } from "@/shared/api/services/cartService";
import { useAuth, useBuyerService } from "@/shared/auth/hooks";
import { CATEGORIES } from "@/shared/constants/categories";
import { cn } from "@/lib/utils";

import { cartKeys } from "@/features/cart/queries/keys";
import { PasswordChangeDialog } from "@/features/mypage/components/PasswordChangeDialog";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

const MOBILE_MENU_ITEM_CLASS =
  "block rounded-md px-4 py-3 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100";

interface UserMenuItem {
  label: string;
  href?: string;
  action?: "password-change" | "logout";
  destructive?: boolean;
}

const USER_MENU_ITEMS: UserMenuItem[] = [
  { label: "마이페이지", href: "/mypage" },
  { label: "주문내역", href: "/orders" },
  { label: "입점 초대", href: "/mypage/referral" },
  { label: "비밀번호 변경", action: "password-change" },
];

type CategoryLike = (typeof CATEGORIES)[number];

function getCategoryHref(cat: CategoryLike): string {
  return (
    cat.href ??
    (cat.id === 0
      ? "/shopping"
      : `/shopping?idCategory=${cat.id}${cat.defaultOrder ? `&order=${cat.defaultOrder}` : ""}`)
  );
}

function CategoryTabs({ onHoverCategory }: { onHoverCategory: (id: number | null) => void }) {
  const searchParams = useSearchParams();
  const activeCategory = Number(searchParams.get("idCategory") ?? "0");

  return (
    <nav aria-label="카테고리" className="flex justify-center gap-6 overflow-x-auto scrollbar-hide">
      {CATEGORIES.map((cat) => {
        const isActive = activeCategory === cat.id;
        const href = getCategoryHref(cat);

        return (
          <Link
            key={cat.id}
            href={href}
            suppressHydrationWarning
            className={cn(
              "shrink-0 whitespace-nowrap py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "border-b-2 border-foreground text-foreground"
                : cat.highlight
                  ? "text-red-500 hover:text-red-600"
                  : "text-neutral-500 hover:text-foreground",
            )}
            onMouseEnter={() => {
              if (cat.subcategories && cat.subcategories.length > 0) {
                onHoverCategory(cat.id);
              } else {
                onHoverCategory(null);
              }
            }}
          >
            {cat.label}
          </Link>
        );
      })}
    </nav>
  );
}

function SubcategoryPanel({
  hoveredCategory,
  onLeave,
}: {
  hoveredCategory: number | null;
  onLeave: () => void;
}) {
  if (hoveredCategory === null) return null;

  const category = CATEGORIES.find((c) => c.id === hoveredCategory);
  if (!category?.subcategories || category.subcategories.length === 0) return null;

  return (
    <div className="border-b" onMouseLeave={onLeave}>
      <div className="mx-auto max-w-screen-xl px-4">
        <div className="flex justify-center gap-6 py-3">
          {category.subcategories.map((sub) => (
            <Link
              key={sub.id}
              href={`/shopping?idCategory=${sub.id}`}
              className="text-[13px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {sub.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MainHeader() {
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const { isAuthenticated, user, isLoading, logout } = useAuth();

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const q = searchQuery.trim();
      if (q) {
        router.push(`/shopping?q=${encodeURIComponent(q)}`);
      }
    },
    [searchQuery, router],
  );
  const cartService = useBuyerService(createCartService);

  const { data: cartData } = useQuery({
    queryKey: cartKeys.count(),
    queryFn: () => cartService.getCartCount(),
    enabled: isAuthenticated,
  });

  const cartCount = cartData?.data?.cartcount ?? 0;

  return (
    <header
      className="sticky top-0 z-50 bg-background"
      onMouseLeave={() => setHoveredCategory(null)}
    >
      {/* Line 1: Logo + Search + Icons — 전체 너비 */}
      <div className="px-4 md:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Left: Logo */}
          <Link href="/">
            <img src="/logo.png" alt="DANGIL" className="w-[80px] h-auto" />
          </Link>

          {/* Center: Search bar (desktop only) */}
          <form onSubmit={handleSearch} className="hidden flex-1 items-center px-4 md:flex">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="브랜드, 상품 검색"
                className="h-9 rounded-full pl-9 text-sm"
              />
            </div>
          </form>

          {/* Right: Icons */}
          <div className="flex items-center gap-1">
            {/* Mobile Search */}
            <Button variant="ghost" size="icon" aria-label="검색" className="md:hidden" asChild>
              <Link href="/search">
                <Search className="h-5 w-5" />
              </Link>
            </Button>

            {/* Wishlist */}
            <Button variant="ghost" size="icon" aria-label="위시리스트" asChild>
              <Link href="/wishlist">
                <Heart className="h-5 w-5" />
              </Link>
            </Button>

            {/* Cart */}
            <Button variant="ghost" size="icon" aria-label="장바구니" className="relative" asChild>
              <Link href="/cart">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <Badge
                    className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full p-0 text-[10px]"
                    variant="default"
                  >
                    {cartCount}
                  </Badge>
                )}
              </Link>
            </Button>

            {/* User: Desktop */}
            <div className="hidden md:block">
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : isAuthenticated ? (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="내 계정">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {user?.businessName?.charAt(0) ?? <User className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40 bg-background">
                    <DropdownMenuItem asChild>
                      <Link href="/mypage">마이페이지</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/orders">주문내역</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/mypage/referral">입점 초대</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setPwDialogOpen(true)}>
                      비밀번호 변경
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onSelect={logout}>
                      로그아웃
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" asChild>
                    <Link href="/login">로그인</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link href="/signup">회원가입</Link>
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile: Hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="메뉴 열기">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="sr-only">메뉴</SheetTitle>
                <nav
                  aria-label="메인 메뉴"
                  className="mt-8 flex flex-col gap-1 overflow-y-auto max-h-[calc(100dvh-4rem)]"
                >
                  {CATEGORIES.map((cat) => (
                    <SheetClose key={cat.id} asChild>
                      <Link
                        href={getCategoryHref(cat)}
                        className={cn(
                          "rounded-md px-4 py-3 text-sm font-medium transition-colors hover:bg-neutral-100",
                          cat.highlight
                            ? "text-red-500"
                            : "text-neutral-700 hover:text-neutral-950",
                        )}
                      >
                        {cat.label}
                      </Link>
                    </SheetClose>
                  ))}
                  <div className="mt-4 border-t pt-4">
                    {isLoading ? (
                      <div className="px-4 py-3">
                        <Skeleton className="h-5 w-24" />
                      </div>
                    ) : isAuthenticated ? (
                      <>
                        {USER_MENU_ITEMS.map((item) =>
                          item.href ? (
                            <SheetClose key={item.label} asChild>
                              <Link href={item.href} className={MOBILE_MENU_ITEM_CLASS}>
                                {item.label}
                              </Link>
                            </SheetClose>
                          ) : item.action === "password-change" ? (
                            <button
                              key={item.label}
                              type="button"
                              className={cn("w-full text-left", MOBILE_MENU_ITEM_CLASS)}
                              onClick={() => {
                                setMobileOpen(false);
                                setPwDialogOpen(true);
                              }}
                            >
                              {item.label}
                            </button>
                          ) : null,
                        )}
                        <button
                          type="button"
                          className="w-full rounded-md px-4 py-3 text-left text-sm font-medium text-destructive transition-colors hover:bg-neutral-100"
                          onClick={() => {
                            logout();
                            setMobileOpen(false);
                          }}
                        >
                          로그아웃
                        </button>
                      </>
                    ) : (
                      <>
                        <SheetClose asChild>
                          <Link href="/login" className={MOBILE_MENU_ITEM_CLASS}>
                            로그인
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link href="/signup" className={MOBILE_MENU_ITEM_CLASS}>
                            회원가입
                          </Link>
                        </SheetClose>
                      </>
                    )}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Line 2: Category tabs — NO border-t, flows naturally */}
      <div className="mx-auto hidden max-w-screen-xl px-4 md:block">
        <Suspense fallback={<div className="h-10" />}>
          <CategoryTabs onHoverCategory={setHoveredCategory} />
        </Suspense>
      </div>

      {/* Line 3: Subcategory panel (shown on hover) */}
      <SubcategoryPanel
        hoveredCategory={hoveredCategory}
        onLeave={() => setHoveredCategory(null)}
      />

      {/* Single bottom border for the whole header */}
      <div className="border-b" />

      <PasswordChangeDialog open={pwDialogOpen} onOpenChange={setPwDialogOpen} />
    </header>
  );
}
